// frontend/src/components/ContentBlock.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import QuestionComponent from './QuestionComponent';
import StreamedHTML from './StreamedHTML';
import TableCellComponent from './TableCellComponent';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ErrorBoundary from './ErrorBoundary';
import logger from '../utils/logger';
import { sortChaptersByCurriculumOrder } from '../utils/courseLock';
import { retryWithBackoff, validateAIResponse, getErrorMessage, getFallbackResponse } from '../utils/aiHelpers';
import * as XLSX from 'xlsx';



// Add blockRef to props
function ContentBlock({ block, index, chapterId, totalBlocks, userAnswers, updateAnswer, onInView, blockRef, chapters, currentChapter, chapterProgress, showCreateDeliverable, hasFeedbackRelevantContent = true }) {
  // Use the passed blockRef with useInView
  const { ref: inViewRefInternal, inView } = useInView({ threshold: 0.6 });
  const navigate = useNavigate(); // Add navigate hook

  // Combine the internal ref logic with the external blockRef
  const combinedRef = (el) => {
    inViewRefInternal(el); // Call useInView's ref function
    if (blockRef) { // If an external ref is provided
      if (typeof blockRef === 'function') {
        blockRef(el);
      } else {
        blockRef.current = el;
      }
    }
  };

  // The index prop is just the array position and may not match block.index
  const blockIndex = block.index !== undefined ? block.index : index; // Fallback to index prop if block.index not available
  const existingAnswer = userAnswers[blockIndex];
  const { token, user } = useContext(AuthContext); // Get user for admin check
  const [hoveredTooltip, setHoveredTooltip] = useState(null); // This state remains here

  // For AI chat functionality
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatInitializedRef = useRef(false);
  
  // Chat resize functionality
  const [chatHeight, setChatHeight] = useState(() => {
    // Initialize with a responsive height based on viewport
    const defaultHeight = Math.min(500, window.innerHeight * 0.6);
    // Try to load from localStorage
    const saved = localStorage.getItem(`chatHeight_${blockIndex}`);
    return saved ? parseInt(saved, 10) : defaultHeight;
  });
  const [maxChatHeight, setMaxChatHeight] = useState(window.innerHeight * 0.8);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);
  
  // Add state for table maximization
  const [isTableMaximized, setIsTableMaximized] = useState(false);

  // ------------------ AI Chat Effects ------------------
  useEffect(() => {
    // Initialize chat messages only once when component mounts or block changes
    // Don't re-sync with existingAnswer after initialization to prevent disappearing chat
    if (block.blockType === 'ai_chat' && !chatInitializedRef.current) {
      // Load existing chat from userAnswers if available
      if (Array.isArray(existingAnswer) && existingAnswer.length > 0) {
        setChatMessages(existingAnswer);
        chatInitializedRef.current = true;
      } 
      // Show welcome message if no existing chat
      else if (block.content?.welcomeMessage) {
        setChatMessages([{ role: 'assistant', content: block.content.welcomeMessage }]);
        chatInitializedRef.current = true;
      }
    }
  }, [block.blockType, block.content?.welcomeMessage, existingAnswer]);

  // Reset initialization flag when block changes (different block)
  useEffect(() => {
    chatInitializedRef.current = false;
  }, [block.index]);

  useEffect(() => {
    // Scroll down on new chat messages within the chat container only
    // Use a small delay to ensure DOM has updated
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current && chatContainerRef.current) {
        // Scroll within the chat container, not the whole page
        // Use scrollTop to scroll the container, not scrollIntoView which affects the page
        const container = chatContainerRef.current;
        const endElement = messagesEndRef.current;
        if (container && endElement) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }, 100);
    
    return () => clearTimeout(scrollTimer);
  }, [chatMessages]);

  useEffect(() => {
    // If AI is typing but block not in view, scroll to it (but only if really needed)
    // Don't scroll if the block is already mostly visible
    if (block.blockType === 'ai_chat' && isTyping && !inView) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const currentBlock = combinedRef.current;
        if (currentBlock) {
          // Use 'nearest' to avoid aggressive scrolling
          currentBlock.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }
      });
    }
  }, [isTyping, block.blockType, inView]);

  // Handle chat resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const deltaY = e.clientY - resizeStartY.current;
      const newHeight = Math.max(200, Math.min(maxChatHeight, resizeStartHeight.current + deltaY));
      setChatHeight(newHeight);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        // Save to localStorage
        localStorage.setItem(`chatHeight_${blockIndex}`, chatHeight.toString());
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, blockIndex, chatHeight, maxChatHeight]);

  // Update chat height on window resize to maintain responsiveness
  useEffect(() => {
    if (block.blockType !== 'ai_chat') return;
    
    const handleResize = () => {
      const newMaxHeight = window.innerHeight * 0.8;
      setMaxChatHeight(newMaxHeight);
      if (chatHeight > newMaxHeight) {
        setChatHeight(newMaxHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    // Set initial max height
    setMaxChatHeight(window.innerHeight * 0.8);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [block.blockType, chatHeight]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = chatHeight;
  };

  // Effect to call the callback when the block comes into view
  useEffect(() => {
    if (inView && onInView) {
      onInView(index);
    }
  }, [inView, index, onInView]);

  // ------------------ AI Chat: Send Message ------------------
  const handleSendMessage = async () => {
    if (!userInput.trim()) return;
    const newMessages = [...chatMessages, { role: 'user', content: userInput }];

    setChatMessages(newMessages);
    updateAnswer(blockIndex, newMessages);
    setUserInput("");
    setIsTyping(true);

    // Scroll to bottom of chat container immediately (user message)
    // Use scrollTop to avoid affecting page scroll
    setTimeout(() => {
      if (chatContainerRef.current) {
        const container = chatContainerRef.current;
        container.scrollTop = container.scrollHeight;
      }
    }, 50);

    try {
      const response = await retryWithBackoff(
        async () => {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/api/chat/message`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              chapterId,
              systemPrompt: block.content?.systemPrompt || 'You are a helpful assistant.',
              messages: newMessages,
              currentPage: 'chapter',
              currentBlockIndex: blockIndex,
              currentContentBlockText: null, // TODO: Could be enhanced to include current block text
              currentFeedback: null
            })
          });

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            const error = new Error(errorData.error || `HTTP ${res.status}`);
            error.status = res.status;
            throw error;
          }

          return res;
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          shouldRetry: (error) => error.status === 429 || (error.status >= 500 && error.status < 600)
        }
      );

      const data = await response.json();
      
      // Validate response
      const validation = validateAIResponse(data, 'message');
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid response format');
      }

      const updatedMessages = [...newMessages, { role: 'assistant', content: validation.data }];
      setChatMessages(updatedMessages);
      updateAnswer(blockIndex, updatedMessages);
    } catch (error) {
      logger.error('Error in AI chat:', error);
      const errorMsg = getErrorMessage(error);
      const fallback = [...newMessages, { 
        role: 'assistant', 
        content: errorMsg,
        isError: true,
        canRetry: true
      }];
      setChatMessages(fallback);
      updateAnswer(blockIndex, fallback);
    } finally {
      setIsTyping(false);
      // Scroll to bottom after response is received (within container only)
      setTimeout(() => {
        if (chatContainerRef.current) {
          const container = chatContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  };

  // ------------------ TODO: Debug Table: Single Auto-fill for entire table ------------------
  const handleAutoFillTable = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/autofill/${blockIndex}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          chapterId,
          tableData: block.content || {}, // send entire table data from content
        }),
      });

      const data = await response.json();
      if (response.ok) {
        // data.autoFill should contain the entire updated table
        // We'll replace block.data with the returned one
        const updatedTable = data.autoFill; 
        // e.g. { headings: [...], rows: [...], ... }
        logger.info(`updatedTable: ${JSON.stringify(updatedTable)}`);
        // Then call updateAnswer to store it
        // (Might want to ensure we keep autoFillCells/frozenCells if GPT didn't remove them)
        updateAnswer(blockIndex, updatedTable);
      } else {
        logger.error("Error generating auto-fill:", data.error);
      }
    } catch (err) {
      logger.error("Error during auto-fill call:", err);
    }
  };

  // ------------------ Render Logic ------------------
  const renderContent = () => {
    // Updated to use blockType instead of type
    switch (block.blockType) {
      case 'text':
        return (
          <div className="overflow-auto max-h-full min-w-0">
            <StreamedHTML
              htmlString={block.content?.text || ''}
              speed={20} // Adjust speed as needed (milliseconds per character)
              streamIncrement={3} // Number of characters to reveal per frame
              startCondition={inView}
              className="my-4 px-4 font-body text-3xl text-gray-900 dark:text-gray-100 break-words [&_ol]:list-decimal [&_ol]:pl-8 [&_ol]:list-outside [&_ul]:list-disc [&_ul]:pl-8 [&_ul]:list-outside [&_li]:mb-0.5 [&_li]:ml-0 [&_h1]:text-5xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-2 [&_h1]:block [&_h2]:text-4xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:block [&_h3]:text-[1.75rem] [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:block [&_h4]:text-[1.5rem] [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:block [&_h5]:text-xl [&_h5]:font-medium [&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:block [&_h6]:text-lg [&_h6]:font-medium [&_h6]:mt-2 [&_h6]:mb-1 [&_h6]:block"
            />
          </div>
        );

      case 'image':
        // Handle both object format {media: {url: ...}} and string format
        let imageUrl = '';
        if (block.content?.media) {
          if (typeof block.content.media === 'string') {
            imageUrl = block.content.media;
          } else if (typeof block.content.media === 'object' && block.content.media.url) {
            imageUrl = block.content.media.url;
          }
        } else if (block.content?.text) {
          imageUrl = block.content.text;
        }
        
        // If URL is relative (starts with /api/uploads), prepend API URL
        // If it already starts with http, use as-is
        // If it starts with / but not /api, it's a relative path
        let fullImageUrl = imageUrl;
        if (imageUrl) {
          if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            fullImageUrl = imageUrl;
          } else if (imageUrl.startsWith('/api/')) {
            fullImageUrl = `${import.meta.env.VITE_API_URL}${imageUrl}`;
          } else if (imageUrl.startsWith('/')) {
            fullImageUrl = `${import.meta.env.VITE_API_URL}${imageUrl}`;
          } else {
            // Relative path without leading slash
            fullImageUrl = `${import.meta.env.VITE_API_URL}/${imageUrl}`;
          }
        }
        
        return (
          <div className="my-4 mx-auto flex justify-center">
            {fullImageUrl ? (
              <img 
                src={fullImageUrl} 
                alt={`Image ${index + 1}`} 
                className="max-w-full h-auto rounded-lg shadow-md"
                onError={(e) => {
                  logger.error('Image load error:', fullImageUrl);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="text-gray-400 dark:text-gray-600">No image available</div>
            )}
          </div>
        );

      case 'question':
      case 'multi_choice':
        // Map the new schema structure to QuestionComponent's expected format
        const questionData = {
          type: block.content?.questionType || (block.blockType === 'multi_choice' ? 'multiple_choice' : 'short_answer'),
          prompt: block.content?.prompt || '',
          options: block.content?.options || [],
          ...block.content // Include any other fields
        };
        return (
          <div className="my-4 font-body dark:text-gray-200 h-full flex flex-col">
            <QuestionComponent
              key={`question-${blockIndex}`}
              question={questionData}
              chapterId={chapterId}
              contentBlockId={blockIndex}
              existingAnswer={existingAnswer}
              onAnswerChange={updateAnswer}
            />
          </div>
        );

      case 'exercise': {
        // Exercise blocks can contain tables - check if content has table structure
        const isTable = block.content && (block.content.headings || block.content.rows);
        if (!isTable) {
          // Not a table, render as exercise/prompt
          return (
            <div className="my-4 font-body text-3xl dark:text-gray-200">
              {block.content?.prompt && <p className="mb-4">{block.content.prompt}</p>}
              {block.content?.text && <p className="mb-4">{block.content.text}</p>}
              {/* Display previous answer in review mode */}
              {existingAnswer && (
                <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600">
                  <p className="text-sm font-semibold mb-2 text-gray-600 dark:text-gray-400">Your Previous Answer:</p>
                  <div className="text-gray-800 dark:text-gray-200">
                    {typeof existingAnswer === 'string' ? (
                      <p className="whitespace-pre-wrap">{existingAnswer}</p>
                    ) : Array.isArray(existingAnswer) ? (
                      <ul className="list-disc list-inside space-y-1">
                        {existingAnswer.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    ) : typeof existingAnswer === 'object' ? (
                      <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(existingAnswer, null, 2)}</pre>
                    ) : (
                      <p>{String(existingAnswer)}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        // It's a table - handle as table
        const initialTableData = (() => {
          const savedAnswer = userAnswers[blockIndex];
          // If we have saved answer with table structure, use it (for review mode)
          if (savedAnswer && typeof savedAnswer === 'object' && savedAnswer.headings) {
            // Merge saved answer with block.content to ensure we have all structural properties
            return {
              headings: savedAnswer.headings || block.content.headings || [],
              rows: savedAnswer.rows || block.content.rows || [],
              frozenCells: savedAnswer.frozenCells || block.content.frozenCells || {},
              autoFillCells: savedAnswer.autoFillCells || block.content.autoFillCells || {},
              tooltips: savedAnswer.tooltips || block.content.tooltips || {},
              fixedColumns: savedAnswer.fixedColumns || block.content.fixedColumns || {},
              autoFillPrompt: savedAnswer.autoFillPrompt || block.content.autoFillPrompt || block.metadata?.helpText || '',
              textContent: savedAnswer.textContent || block.content.textContent || '',
              selectedColumn: savedAnswer.selectedColumn !== undefined ? savedAnswer.selectedColumn : (block.content.selectedColumn !== undefined ? block.content.selectedColumn : null),
            };
          }
          // Parse table structure from block.content
          return {
            headings: block.content.headings || [],
            rows: block.content.rows || [],
            frozenCells: block.content.frozenCells || {},
            autoFillCells: block.content.autoFillCells || {},
            tooltips: block.content.tooltips || {},
            fixedColumns: block.content.fixedColumns || {},
            autoFillPrompt: block.content.autoFillPrompt || block.metadata?.helpText || '',
            textContent: block.content.textContent || '',
            selectedColumn: block.content.selectedColumn || null,
          };
        })();

        const [tableData, setTableData] = useState(initialTableData);
        
        // Undo/Redo history - initialize with initial state
        const [history, setHistory] = useState(() => [JSON.parse(JSON.stringify(initialTableData))]);
        const [historyIndex, setHistoryIndex] = useState(0);
        const historyRef = useRef({ 
          history: [JSON.parse(JSON.stringify(initialTableData))], 
          historyIndex: 0 
        });
        
        // Update refs when state changes
        useEffect(() => {
          historyRef.current = { history, historyIndex };
        }, [history, historyIndex]);
        
        // Reset history when tableData is updated from external source (e.g., userAnswers change)
        useEffect(() => {
          // Only reset if this is a significant external change (not from our own edits)
          // We'll detect this by checking if the change came from the useEffect that syncs with userAnswers
          const newHistory = [JSON.parse(JSON.stringify(tableData))];
          setHistory(newHistory);
          setHistoryIndex(0);
          historyRef.current = { history: newHistory, historyIndex: 0 };
        }, [block.content, userAnswers, blockIndex]); // Reset history when external data changes
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [saveStatus, setSaveStatus] = useState('');
        const [autoFillingCells, setAutoFillingCells] = useState({});
        const firstRender = useRef(true);
        const saveTimerRef = useRef(null); // Track debounce timer for cell changes

        useEffect(() => {
          // block.content is the definitive structure (headings, frozenCells, autoFillCells, tooltips, autoFillPrompt)
          // userAnswers[blockIndex]?.rows contains the user's actual cell inputs.
        
          const userSavedData = userAnswers[blockIndex];
        
          // Start with the latest definition from block.content for all structural properties
          let newTableState = {
            headings: block.content.headings || [],
            rows: [], // Will be populated based on user answers or block definition
            frozenCells: block.content.frozenCells || {},
            autoFillCells: block.content.autoFillCells || {},
            tooltips: block.content.tooltips || {},
            fixedColumns: block.content.fixedColumns || {},
            autoFillPrompt: block.content.autoFillPrompt || block.metadata?.helpText || '',
            textContent: block.content.textContent || '',
            selectedColumn: userSavedData?.selectedColumn !== undefined ? userSavedData.selectedColumn : (block.content.selectedColumn !== undefined ? block.content.selectedColumn : null),
          };
        
          const definitionRows = block.content.rows || [];
          const definitionHeadings = newTableState.headings;
          const numCols = definitionHeadings.length;
        
          // Use user's saved rows if available, otherwise use definition rows from block.content
          const rowsSource = (userSavedData?.rows && userSavedData.rows.length > 0) ? userSavedData.rows : definitionRows;
        
          newTableState.rows = rowsSource.map(sourceRow => {
            const newRow = Array(numCols).fill(''); // Initialize new row based on definitive column count
            if (Array.isArray(sourceRow)) {
              for (let i = 0; i < numCols; i++) {
                if (sourceRow[i] !== undefined) {
                  newRow[i] = sourceRow[i]; // Take cell value from source
                }
              }
            }
            return newRow;
          });
        
          // If the number of rows in block.data is different from rowsSource, adjust.
          // This ensures that if admin adds/removes rows, the table reflects that structure.
          if (definitionRows.length !== newTableState.rows.length) {
            const adjustedRows = Array(definitionRows.length).fill(null).map((_, rIndex) => {
              if (newTableState.rows[rIndex]) { // If user data exists for this row index
                // Ensure it has the correct number of columns
                const rowToAdjust = newTableState.rows[rIndex];
                const finalAdjustedRow = Array(numCols).fill('');
                for(let cIndex = 0; cIndex < numCols; cIndex++) {
                  if (rowToAdjust[cIndex] !== undefined) {
                    finalAdjustedRow[cIndex] = rowToAdjust[cIndex];
                  }
                }
                return finalAdjustedRow;
              }
              // If no user data for this row index, use the definition row (padded)
              const defRow = definitionRows[rIndex] || [];
              const finalDefRow = Array(numCols).fill('');
              for(let cIndex = 0; cIndex < numCols; cIndex++) {
                if (defRow[cIndex] !== undefined) {
                  finalDefRow[cIndex] = defRow[cIndex];
                }
              }
              return finalDefRow;
            });
            newTableState.rows = adjustedRows;
          }

          // Only update the local state if the newly constructed state is different
          // from the current tableData. This prevents unnecessary re-renders.
          // IMPORTANT: We only update when userAnswers or block.content changes (external changes),
          // NOT when tableData changes (local edits). This prevents overwriting user's in-progress edits.
          const currentStr = JSON.stringify(tableData || {});
          const newStr = JSON.stringify(newTableState);
          
          if (currentStr !== newStr) {
            setTableData(newTableState);
          }
        }, [block.content, userAnswers, blockIndex]); // Removed tableData from dependencies to prevent overwriting local edits

        // Autosave effect - save table data when it changes (works for both resume and review)
        useEffect(() => {
          return () => {
            if (saveTimerRef.current) {
              clearTimeout(saveTimerRef.current);
            }
          };
        }, []);

        // Define saveTableToServer before it's used
        const saveTableToServer = async (dataToSave) => {
          try {
            setIsSubmitting(true);
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/user/submit-answer`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                chapterId,
                contentBlockIndex: blockIndex,
                answer: dataToSave, 
              }),
            });
  
            if (!res.ok) throw new Error('Failed to save table');
            setSaveStatus('Saved.');
            // setTimeout(() => setSaveStatus(''), 2000);
          } catch (error) {
            logger.error('Error saving table:', error);
            setSaveStatus('Error saving table');
            // setTimeout(() => setSaveStatus(''), 3000);
          } finally {
            setIsSubmitting(false);
          }
        };

        // Add to history helper
        const addToHistory = (newTableData) => {
          const currentHistory = historyRef.current.history;
          const currentIndex = historyRef.current.historyIndex;
          
          // Remove any history after current index (when user makes new change after undo)
          const newHistory = currentHistory.slice(0, currentIndex + 1);
          
          // Add new state to history (limit to 50 entries)
          const deepCopy = JSON.parse(JSON.stringify(newTableData));
          newHistory.push(deepCopy);
          
          let newIndex = currentIndex + 1;
          if (newHistory.length > 50) {
            newHistory.shift();
            newIndex = 49; // Adjust index if we removed the first item
          }
          
          setHistory(newHistory);
          setHistoryIndex(newIndex);
        };

        const handleCellChange = (rowIndex, colIndex, newValue) => {
          // Save current state to history before making change
          addToHistory(tableData);
          
          const updated = { ...tableData, rows: [...(tableData.rows || [])] };
          updated.rows[rowIndex] = [...(updated.rows[rowIndex] || [])];
          updated.rows[rowIndex][colIndex] = newValue;
          setTableData(updated);
          
          // Set saving status
          setSaveStatus('Saving...');
          
          // Immediately update parent state (this also saves to server via updateAnswer)
          if (updateAnswer) {
            // Try to handle async updateAnswer if it returns a promise
            const result = updateAnswer(blockIndex, updated);
            if (result && typeof result.then === 'function') {
              result
                .then(() => {
                  setSaveStatus('Saved.');
                  setTimeout(() => setSaveStatus(''), 2000);
                })
                .catch((error) => {
                  logger.error('Error saving cell:', error);
                  setSaveStatus('Error saving');
                  setTimeout(() => setSaveStatus(''), 3000);
                });
            } else {
              // If updateAnswer doesn't return a promise, assume it's saved after a short delay
              setTimeout(() => {
                setSaveStatus('Saved.');
                setTimeout(() => setSaveStatus(''), 2000);
              }, 300);
            }
          }
          
          // Note: We don't need saveTableToServer here because updateAnswer already saves to server
          // The debounced save was causing duplicate saves and race conditions
        };

        // Undo handler
        const handleUndo = (e) => {
          // Check if Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
          if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
            const currentHistory = historyRef.current.history;
            const currentIndex = historyRef.current.historyIndex;
            
            if (currentIndex > 0) {
              e.preventDefault();
              e.stopPropagation();
              
              const newIndex = currentIndex - 1;
              const previousState = JSON.parse(JSON.stringify(currentHistory[newIndex]));
              
              setHistoryIndex(newIndex);
              setTableData(previousState);
              
              // Set saving status
              setSaveStatus('Saving...');
              
              // Update parent state
              if (updateAnswer) {
                const result = updateAnswer(blockIndex, previousState);
                if (result && typeof result.then === 'function') {
                  result
                    .then(() => {
                      setSaveStatus('Saved.');
                      setTimeout(() => setSaveStatus(''), 2000);
                    })
                    .catch((error) => {
                      logger.error('Error saving undo:', error);
                      setSaveStatus('Error saving');
                      setTimeout(() => setSaveStatus(''), 3000);
                    });
                } else {
                  setTimeout(() => {
                    setSaveStatus('Saved.');
                    setTimeout(() => setSaveStatus(''), 2000);
                  }, 300);
                }
              }
            }
          }
        };

        // Redo handler (Cmd+Shift+Z or Ctrl+Shift+Z)
        const handleRedo = (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
            const currentHistory = historyRef.current.history;
            const currentIndex = historyRef.current.historyIndex;
            
            if (currentIndex < currentHistory.length - 1) {
              e.preventDefault();
              e.stopPropagation();
              
              const newIndex = currentIndex + 1;
              const nextState = JSON.parse(JSON.stringify(currentHistory[newIndex]));
              
              setHistoryIndex(newIndex);
              setTableData(nextState);
              
              // Set saving status
              setSaveStatus('Saving...');
              
              // Update parent state
              if (updateAnswer) {
                const result = updateAnswer(blockIndex, nextState);
                if (result && typeof result.then === 'function') {
                  result
                    .then(() => {
                      setSaveStatus('Saved.');
                      setTimeout(() => setSaveStatus(''), 2000);
                    })
                    .catch((error) => {
                      logger.error('Error saving redo:', error);
                      setSaveStatus('Error saving');
                      setTimeout(() => setSaveStatus(''), 3000);
                    });
                } else {
                  setTimeout(() => {
                    setSaveStatus('Saved.');
                    setTimeout(() => setSaveStatus(''), 2000);
                  }, 300);
                }
              }
            }
          }
        };

        // Add keyboard event listeners for undo/redo
        useEffect(() => {
          const handleKeyDown = (e) => {
            // Only handle if not typing in an input/textarea
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
              // Allow undo/redo in textarea
              if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                handleUndo(e);
                handleRedo(e);
              }
              return;
            }
            
            handleUndo(e);
            handleRedo(e);
          };

          window.addEventListener('keydown', handleKeyDown);
          return () => {
            window.removeEventListener('keydown', handleKeyDown);
          };
        }, []); // Empty deps - we use refs for current state

        const handleAutoFillTable = async () => {
          if (!tableData || !tableData.rows || !tableData.autoFillCells) return;

          const cellsToLoad = {};
          (tableData.rows || []).forEach((row, rIndex) => {
            (row || []).forEach((cell, cIndex) => {
              const cellKey = `${rIndex}-${cIndex}`;
              // Mark for loading if it's an autoFillCell
              // Allow replacing existing content - user can click Auto-Fill again to replace
              if (tableData.autoFillCells[cellKey]) {
                cellsToLoad[cellKey] = true;
              }
            });
          });

          if (Object.keys(cellsToLoad).length === 0) {
            setSaveStatus("No cells marked for auto-fill.");
            setTimeout(() => setSaveStatus(''), 2000);
            return;
          }
          
          setAutoFillingCells(cellsToLoad);
          setIsSubmitting(true);
          setSaveStatus('Auto-filling...');

          try {
            const response = await retryWithBackoff(
              async () => {
                const res = await fetch(`${import.meta.env.VITE_API_URL}/api/autofill/${blockIndex}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    chapterId,
                    tableData, 
                  }),
                });

                if (!res.ok) {
                  const errorData = await res.json().catch(() => ({}));
                  const error = new Error(errorData.error || `HTTP ${res.status}`);
                  error.status = res.status;
                  throw error;
                }

                return res;
              },
              {
                maxRetries: 2, // Fewer retries for auto-fill since it's a longer operation
                initialDelay: 2000,
                shouldRetry: (error) => error.status === 429 || (error.status >= 500 && error.status < 600)
              }
            );

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let streamedContent = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop(); // Keep incomplete line in buffer

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    
                    if (data.type === 'progress' && data.content) {
                      streamedContent += data.content;
                      // Update status to show streaming progress
                      setSaveStatus(`Auto-filling... (${streamedContent.length} characters received)`);
                    } else if (data.type === 'complete' && data.autoFill) {
                      // Validate table response
                      const validation = validateAIResponse(data, 'table');
                      if (!validation.valid) {
                        throw new Error(validation.error || 'Invalid table response format');
                      }
                      
                      // Final result received
                      const updatedTable = validation.data;
                      setTableData(updatedTable);
                      
                      // Immediately update parent state and save to server
                      if (updateAnswer) { 
                        updateAnswer(blockIndex, updatedTable);
                      }
                      
                      setSaveStatus('Auto-fill complete.');
                      setAutoFillingCells({});
                      setIsSubmitting(false);
                      setTimeout(() => setSaveStatus(''), 3000);
                      return;
                    } else if (data.type === 'error') {
                      throw new Error(data.error || 'Auto-fill error');
                    }
                  } catch (parseError) {
                    logger.error('Error parsing SSE data:', parseError);
                  }
                }
              }
            }
          } catch (err) {
            logger.error("Error during auto-fill:", err);
            const errorMsg = getErrorMessage(err);
            setSaveStatus(`Auto-fill error: ${errorMsg}`);
            setAutoFillingCells({});
            setIsSubmitting(false);
            setTimeout(() => setSaveStatus(''), 5000); // Show error longer
          }
        };

        const COLUMN_WIDTH = 350; // Define a constant column width in pixels

        const calculateStickyLeftOffset = (currentColIndex, fixedColumnsMap) => {
          let offset = 0;
          for (let i = 0; i < currentColIndex; i++) {
            if (fixedColumnsMap && fixedColumnsMap[i]) {
              offset += COLUMN_WIDTH;
            }
          }
          return `${offset}px`;
        };

        if (!tableData || !tableData.headings || !Array.isArray(tableData.rows)) {
          return (
            <div className="my-4 px-4 font-body text-3xl text-gray-500 dark:text-gray-400">
              {!tableData ? 'Loading table data...' : 'Table structure is invalid. Please check the content block configuration.'}
            </div>
          );
        }

        // Function to render the table content
        const renderTableContent = () => (
          <table className="min-w-full w-full text-left border-collapse">
            <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-600">
              <tr>
                {(tableData.headings || []).map((heading, i) => {
                  const isFixed = tableData.fixedColumns?.[i];
                  const isSelected = tableData.selectedColumn === i;
                  const thStyle = {
                    width: `${COLUMN_WIDTH}px`,
                    minWidth: `${COLUMN_WIDTH}px`,
                  };
                  if (isFixed) {
                    thStyle.position = 'sticky';
                    thStyle.left = calculateStickyLeftOffset(i, tableData.fixedColumns);
                    thStyle.zIndex = 2;
                    thStyle.backgroundColor = 'rgb(243 244 246)';
                  }
                  return (
                    <th
                      key={i}
                      style={thStyle}
                      className={`px-6 py-3 border-b border-gray-200 text-gray-600 font-semibold text-sm tracking-wider cursor-pointer transition-colors dark:text-white dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-500 ${
                        isSelected ? 'bg-[#95dbcb] dark:bg-customGreen' : 'hover:bg-gray-200 dark:hover:bg-gray-500 dark:hover:text-gray-200'
                      }`}
                      onClick={() => handleColumnSelect(i)}
                      title={`Click to ${isSelected ? 'deselect' : 'select'} column`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{heading}</span>
                        {isSelected && (
                          <svg 
                            className="w-4 h-4 text-black ml-2 dark:text-white" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:divide-gray-600">
              {(tableData.rows || []).map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-500`}
                >
                  {(row || []).map((cell, colIndex) => {
                    const cellKey = `${rowIndex}-${colIndex}`;
                    const isCellFrozenByFreezeFeature = tableData.frozenCells?.[cellKey];
                    const tooltipData = tableData.tooltips?.[cellKey];
                    const isCurrentlyAutoFilling = autoFillingCells[cellKey] === true;
                    const isColumnFixed = tableData.fixedColumns?.[colIndex];
                    const isColumnSelected = tableData.selectedColumn === colIndex;
                    const cellBaseStyle = {
                      width: `${COLUMN_WIDTH}px`,
                      minWidth: `${COLUMN_WIDTH}px`,
                    };
                    let cellFinalStyle = { ...cellBaseStyle };

                    if (isColumnFixed) {
                      cellFinalStyle = {
                        ...cellFinalStyle,
                        position: 'sticky',
                        left: calculateStickyLeftOffset(colIndex, tableData.fixedColumns),
                        zIndex: 1,
                        backgroundColor: isCellFrozenByFreezeFeature || isCurrentlyAutoFilling
                          ? 'rgb(243 244 246)'
                          : isColumnSelected
                          ? 'rgb(213 237 232)' // Light blue for selected column
                          : (rowIndex % 2 === 0 ? 'white' : 'rgb(249 250 251)'),
                      };
                    } else if (isColumnSelected) {
                      cellFinalStyle = {
                        ...cellFinalStyle,
                        backgroundColor: isCellFrozenByFreezeFeature || isCurrentlyAutoFilling
                          ? 'rgb(243 244 246)'
                          : 'rgb(213 237 232)', // Light blue for selected column
                      };
                    }

                    return (
                      <TableCellComponent
                        key={cellKey}
                        cellValue={cell}
                        isFrozen={isCellFrozenByFreezeFeature || isCurrentlyAutoFilling}
                        tooltipData={tooltipData}
                        cellKey={cellKey}
                        onCellChange={handleCellChange}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                        hoveredTooltip={hoveredTooltip}
                        setHoveredTooltip={setHoveredTooltip}
                        isAutoFilling={isCurrentlyAutoFilling}
                        stickyStyle={cellFinalStyle}
                        isColumnSelected={isColumnSelected}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        );

        const handleColumnSelect = (colIndex) => {
          const newSelectedColumn = tableData.selectedColumn === colIndex ? null : colIndex;
          const updated = { 
            ...tableData, 
            selectedColumn: newSelectedColumn
          };
          setTableData(updated);
        };

        // Excel Export Function
        const handleExportToExcel = () => {
          try {
            // Create a new workbook
            const wb = XLSX.utils.book_new();
            
            // Prepare table data FIRST (so it appears as the first tab)
            const tableRows = [];
            
            // Add headers
            const headers = tableData.headings || [];
            tableRows.push(headers);
            
            // Add rows
            (tableData.rows || []).forEach((row) => {
              tableRows.push(row);
            });

            // Filter empty rows and clean null/undefined value
            const filteredRows = tableRows.filter(row =>
              row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')
            );

            const cleanedRows = filteredRows.map(row =>
              headers.map((_, i) => row[i] ?? '') // Only take as many columns as there are headers
            );

            // Create worksheet from table data
            const ws = XLSX.utils.aoa_to_sheet(cleanedRows);
            
            // Apply column widths
            const colWidths = headers.map(() => ({ wch: 20 }));
            ws['!cols'] = colWidths;
            // Set the range of the worksheet to the cleaned rows
            ws['!ref'] = XLSX.utils.encode_range({
              s: { r: 0, c: 0 },
              e: { 
                r: cleanedRows.length - 1, 
                c: Math.max(...cleanedRows.map(r => r.length)) - 1 
              }
            });
            
            // Note: XLSX.js (SheetJS Community Edition) has limited styling support
            // For full styling, you'd need the Pro version. We'll skip styling for now
            // and just ensure the data is exported correctly
            
            // Clean cell values - strip markdown and convert to plain text
            const cleanCellValue = (value) => {
              if (!value) return '';
              const str = String(value);
              // Remove markdown syntax
              return str
                .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
                .replace(/\*(.*?)\*/g, '$1') // Italic
                .replace(/`(.*?)`/g, '$1') // Code
                .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Links
                .replace(/^#+\s+/gm, '') // Headers
                .replace(/^[-*+]\s+/gm, '• ') // List bullets
                .replace(/^\d+\.\s+/gm, '') // Numbered lists
                .replace(/\n{2,}/g, '\n') // Multiple newlines
                .trim();
            };
            
            // Clean all cell values in the worksheet
            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
            for (let row = range.s.r; row <= range.e.r; row++) {
              for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress] && ws[cellAddress].v) {
                  ws[cellAddress].v = cleanCellValue(ws[cellAddress].v);
                  ws[cellAddress].t = 's'; // Ensure it's treated as string
                }
              }
            }
            
            // Add table sheet FIRST (first tab)
            XLSX.utils.book_append_sheet(wb, ws, 'Table Data');
            
            // Prepare chapter context sheet SECOND (second tab)
            const chapterContext = [];

            // Add text content if available
            if (tableData.textContent) {
              chapterContext.push(['Table Context:']);
              // Strip HTML from textContent for Excel
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = tableData.textContent;
              const plainText = tempDiv.textContent || tempDiv.innerText || '';
              chapterContext.push([plainText]);
              chapterContext.push([]); // Empty row
            }

            // Prepare chapter context sheet
            if (currentChapter) {
              chapterContext.push(['Chapter Information']);
              chapterContext.push(['Title:', currentChapter.title || 'N/A']);
              chapterContext.push(['Description:', currentChapter.description || 'N/A']);
              if (currentChapter.moduleId?.name) {
                chapterContext.push(['Module:', currentChapter.moduleId.name]);
              }
              if (currentChapter.moduleId?.unitId?.name) {
                chapterContext.push(['Unit:', currentChapter.moduleId.unitId.name]);
              }
              chapterContext.push([]); // Empty row
            }
            
            // Create context sheet SECOND (second tab)
            if (chapterContext.length > 0) {
              // Filter out empty rows from context too
              const cleanedContext = chapterContext.filter(row => row.length > 0 && row.some(cell => cell !== null && cell !== undefined && cell !== ''));
              const contextWs = XLSX.utils.aoa_to_sheet(cleanedContext);
              contextWs['!cols'] = cleanedContext[0] ? cleanedContext[0].map((_, i) => ({ wch: i === 0 ? 20 : 50 })) : [];

              // Trim context sheet range too
              contextWs['!ref'] = XLSX.utils.encode_range({
                s: { r: 0, c: 0 },
                e: {
                  r: cleanedContext.length - 1,
                  c: Math.max(...cleanedContext.map(r => r.length)) - 1
                }
              });
              XLSX.utils.book_append_sheet(wb, contextWs, 'Chapter Context');
            }
            
            // Generate filename with chapter and timestamp
            const chapterTitle = currentChapter?.title || 'Chapter';
            const sanitizedTitle = chapterTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `${sanitizedTitle}_table_${timestamp}.xlsx`;
            
            // Write and download
            XLSX.writeFile(wb, filename);
            
            logger.info('Table exported to Excel successfully');
          } catch (error) {
            logger.error('Error exporting table to Excel:', error);
            alert('Failed to export table to Excel. Please try again.');
          }
        };

        return (
          <>
            <div className="my-4 px-4 font-body">
              {/* Text Content Section */}
              {tableData.textContent && (
                <div 
                  className="font-body mb-6 text-3xl leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: tableData.textContent }}
                />
              )}
              
              <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
                <button
                  onClick={handleAutoFillTable}
                  className="bg-black text-white px-4 py-2 rounded-md font-body text-sm hover:bg-gray-800 transition disabled:opacity-50 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700"
                  disabled={isSubmitting}
                >
                  Auto-fill Table
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleExportToExcel}
                    className="bg-green-600 text-white px-4 py-2 rounded-md font-body text-sm hover:bg-green-700 transition flex items-center gap-2 dark:bg-green-700 dark:hover:bg-green-800"
                    title="Export table to Excel"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      strokeWidth={1.5} 
                      stroke="currentColor" 
                      className="w-4 h-4"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Export Excel
                  </button>
                  <button
                    onClick={() => setIsTableMaximized(true)}
                    className="bg-white text-black border border-black px-4 py-2 rounded-md font-body text-sm transition flex items-center gap-2 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      strokeWidth={1.5} 
                      stroke="currentColor" 
                      className="w-4 h-4"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                    Maximize
                  </button>
                </div>
              </div>
              
              <div
                className="overflow-x-auto overflow-y-auto shadow-sm rounded-lg border border-gray-200"
                style={{ maxHeight: '425px' }}
              >
                {renderTableContent()}
              </div>
              <div className='text-sm mt-4 h-5 text-gray-500'>Click <b>Auto-fill table</b> to generate responses based on your answers. Cells are editable and save automatically. To re-generate AI content, clear the cell first. Click <b>Export Excel</b> to save a copy before making changes.</div>
              <div className="text-sm mt-4 h-5 text-gray-500">{saveStatus}</div>
            </div>

            {/* Maximized Table Modal */}
            {isTableMaximized && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
                <div className="font-body mx-8 bg-white rounded-lg shadow-xl w-full h-full max-w-full max-h-[90vh] flex flex-col">
                  {/* Modal Header */}
                  <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <button
                      onClick={handleAutoFillTable}
                      className="bg-black text-white px-3 py-1.5 rounded-md text-sm hover:bg-gray-800 transition disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      Auto-fill Table
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={handleExportToExcel}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-green-700 transition flex items-center gap-2"
                        title="Export table to Excel"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          strokeWidth={1.5} 
                          stroke="currentColor" 
                          className="w-4 h-4"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export Excel
                      </button>
                      <button
                        onClick={() => setIsTableMaximized(false)}
                        className="bg-white text-black border border-black border-1 px-3 py-1.5 rounded-md text-sm transition flex items-center gap-1"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          strokeWidth={1.5} 
                          stroke="currentColor" 
                          className="w-4 h-4"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                        </svg>
                        Minimize
                      </button>
                    </div>
                  </div>
                  
                  {/* Modal Content */}
                  <div className="flex-1 overflow-auto p-4">
                    {/* Text Content in Modal */}
                    
                    
                    <div className="overflow-auto border border-gray-200 rounded-lg">
                      {renderTableContent()}
                    </div>
                    <div className='text-sm mt-4 h-5 text-gray-500'>Click <b>Auto-fill table</b> to generate responses based on your answers. Cells are editable and save automatically. To re-generate AI content, clear the cell first. Click <b>Export Excel</b> to save a copy before making changes.</div>
                    {saveStatus && (
                      <div className="text-sm mt-4 text-gray-500">{saveStatus}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      }
        
        

      case 'ai_chat':
        return (
          <ErrorBoundary
            title="AI Chat Error"
            message="An error occurred with the AI chat. Please try again."
          >
            <div className="my-4 w-full max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-md p-4 dark:bg-gray-800 dark:text-gray-200" style={{ position: 'relative' }}>
              <div
                ref={chatContainerRef}
                className="overflow-y-auto mb-2 p-2 border rounded dark:border-gray-600"
                style={{ 
                  height: `${Math.min(chatHeight, maxChatHeight)}px`,
                  minHeight: '200px',
                  maxHeight: `${maxChatHeight}px`
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {chatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'} dark:text-gray-200`}
                  >
                    <div
                      className={`inline-block p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-500 text-white'
                          : msg.isError
                          ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 dark:text-gray-200'
                          : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500'
                      }`}
                    >
                      {msg.role === 'assistant' ? (
                        <>
                          {msg.isError ? (
                            <div className="flex flex-col">
                              <p className="text-sm text-red-700 dark:text-red-300 mb-2 dark:text-gray-200">{msg.content}</p>
                              {msg.canRetry && (
                                <button
                                  onClick={() => {
                                    setChatMessages(prev => prev.filter(m => m !== msg));
                                    handleSendMessage();
                                  }}
                                  className="text-xs bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 text-red-800 dark:text-red-200 px-2 py-1 rounded self-start transition-colors"
                                >
                                  Retry
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:dark:text-gray-800 prose-p:dark:text-gray-800 prose-table:dark:text-gray-800 prose-th:dark:text-gray-900 prose-td:dark:text-gray-800 prose-a:dark:text-blue-600">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {msg.content}
                              </ReactMarkdown>
                            </div>
                          )}
                        </>
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="text-left">
                    <div className="inline-block p-3 rounded-lg bg-gray-200 dark:bg-gray-500 dark:text-gray-200 dark:border-gray-500">
                      <span className="animate-pulse">Typing...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Resize handle */}
              <div
                onMouseDown={handleResizeStart}
                className="h-2 mb-2 cursor-row-resize hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors flex items-center justify-center group"
                style={{ touchAction: 'none' }}
              >
                <div className="w-12 h-1 bg-gray-400 dark:bg-gray-500 rounded group-hover:bg-gray-500 dark:group-hover:bg-gray-400 transition-colors"></div>
              </div>

              <div className="flex">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-grow p-2 border rounded-l dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500"
                  onClick={(e) => e.stopPropagation()}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSendMessage();
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-r"
                  disabled={isTyping}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
          </ErrorBoundary>
        );

      case 'lastSlide': {
        const { adjustedUnansweredIndexes = [] } = block;
        const stillIncomplete = adjustedUnansweredIndexes.length > 0;
        
        // Get progress data
        const progressData = chapterProgress || {};
        const completionPercentage = progressData.completionPercentage || 0;
        const status = progressData.status || 'not_started';
        const isCompleted = status === 'completed';
        const isInProgress = status === 'in_progress';
        
        // Find next chapter by curriculum order (unit → module → chapter)
        const getNextChapter = () => {
          if (!chapters || !currentChapter) return null;
          const sortedChapters = sortChaptersByCurriculumOrder(chapters);
          const currentIndex = sortedChapters.findIndex(ch => ch._id === chapterId);
          if (currentIndex >= 0 && currentIndex < sortedChapters.length - 1) {
            return sortedChapters[currentIndex + 1];
          }
          return null;
        };
        
        const nextChapter = getNextChapter();
        
        return (
          <div className="font-body text-3xl my-4 px-4 text-center dark:text-gray-200">
            {/* Progress Display */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2 text-xl">
                <span className="text-gray-600 dark:text-gray-400">Progress</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
                <div 
                  className={`h-3 rounded-full transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-green-500 dark:bg-green-600' 
                      : isInProgress
                      ? 'bg-blue-500 dark:bg-blue-600'
                      : 'bg-gray-400 dark:bg-gray-500'
                  }`}
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
              <div className="flex justify-center gap-4 mt-2">
                {isCompleted && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                    ✓ Completed
                  </span>
                )}
                {isInProgress && !isCompleted && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                    In Progress
                  </span>
                )}
                {!isInProgress && !isCompleted && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Not Started
                  </span>
                )}
              </div>
            </div>
            {stillIncomplete ? (
              <>
                <p className="mb-4 text-red-500 dark:text-red-400">
                  Please complete slides {adjustedUnansweredIndexes.join(', ')} before accessing Feedback.
                </p>
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = '/profile';
                    }}
                    className="bg-gray-500 dark:bg-gray-600 text-white px-6 py-3 rounded-lg text-2xl font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                  >
                    Back to Home
                  </button>
                  {/* Only show "Continue Anyway" button for admin users in dev mode */}
                  {import.meta.env.DEV && user && (user.role === 'admin' || user.isAdmin === true) && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/feedback/${chapterId}`);
                      }}
                      className="bg-blue-500 dark:bg-blue-600 text-white px-6 py-3 rounded-lg text-2xl font-semibold hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                    >
                      Continue Anyway (Testing)
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="mb-4 text-green-600 dark:text-green-400">
                  {hasFeedbackRelevantContent ? 'All questions answered!' : 'You\'ve reached the end of this chapter.'}
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-6">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = '/profile';
                    }}
                    className="bg-gray-500 dark:bg-gray-600 text-white px-6 py-3 rounded-lg text-2xl font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                  >
                    Back to Home
                  </button>
                  {hasFeedbackRelevantContent && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/feedback/${chapterId}`);
                      }}
                      className="bg-green-600 dark:bg-green-700 text-white px-6 py-3 rounded-lg text-2xl font-semibold hover:bg-green-700 dark:hover:bg-green-800 transition-colors"
                    >
                      View feedback
                    </button>
                  )}
                  {showCreateDeliverable && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate('/deliverables');
                      }}
                      className="bg-indigo-600 dark:bg-indigo-700 text-white px-6 py-3 rounded-lg text-2xl font-semibold hover:bg-indigo-700 dark:hover:bg-indigo-800 transition-colors"
                    >
                      Create deliverable
                    </button>
                  )}
                  {nextChapter && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = `/chapter/${nextChapter._id}`;
                      }}
                      className="bg-blue-600 dark:bg-blue-700 text-white px-6 py-3 rounded-lg text-2xl font-semibold hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
                    >
                      Next Module: {nextChapter.title}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <motion.div
      ref={combinedRef}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0, y: 20 }, // Fade out and slightly down
        visible: { opacity: 1, y: 0 },  // Fade in and to original position
      }}
      transition={{ duration: 0.5, ease: 'easeOut' }} // Smoother transition
      className="content-block flex items-center justify-center relative w-full"
      style={{ height: '100%', flexShrink: 0, overflow: 'auto' }}
    >
      {/* Inner content: full width up to readable max, centered */}
      <div className="w-full max-w-4xl mx-auto min-w-0 max-h-full overflow-x-auto overflow-y-visible pl-14 pr-4">
        {renderContent()}
      </div>
      {/* Slide count removed from here */}
    </motion.div>
  );
}

export default ContentBlock;
